import type { FC } from 'react';

import type { CardProps } from '@xing-com/card';
import type { Maybe, Scalars } from '@xing-com/crate-common-graphql-types';
import { Observed } from '@xing-com/crate-communication-in-view';
import { useAppliedTheme } from '@xing-com/hub';
import { xdlColorBackgroundSecondary } from '@xing-com/tokens';

import { Styled } from './content-card.style';
import { variantToHeadlineFontSize } from './util';

export type ArticleImage = {
  srcSquare?: Maybe<Scalars['URL']['output']>;
  /** #logged-out #soft-logged-in */
  srcSquare2x?: Maybe<Scalars['URL']['output']>;
  /** #logged-out #soft-logged-in */
  srcWide?: Maybe<Scalars['URL']['output']>;
  /** #logged-out #soft-logged-in */
  srcWide2x?: Maybe<Scalars['URL']['output']>;
};

export type ContentCardProps = Omit<CardProps, 'variant'> & {
  variant?: 'default' | 'hero';
  title?: string | null;
  description?: string | null;
  category?: string | null;
  bgColor?: string | null;
  image?: ArticleImage | null;
  readingTime?: number | null;
  onEnter?: () => void;
  onLeave?: () => void;
};

const DEFAULT_BG_COLOR = '#F7F2EB';

export const ContentCard: FC<ContentCardProps> = ({
  variant = 'default',
  bgColor: bgColorProp,
  title,
  image,
  to,
  description,
  category,
  readingTime,
  onEnter = () => 0,
  onLeave = () => 0,
  className,
  ...props
}) => {
  const appliedTheme = useAppliedTheme();
  const isDarkMode = appliedTheme === 'dark';

  const bgColor =
    isDarkMode && !bgColorProp
      ? xdlColorBackgroundSecondary
      : !bgColorProp || variant === 'default'
        ? DEFAULT_BG_COLOR
        : bgColorProp;

  const headlineSize = variantToHeadlineFontSize[variant];

  return (
    <Styled.Card
      minHeight="100%"
      $bgColor={bgColor}
      $variant={variant}
      className={className}
      {...props}
    >
      <Observed onEnter={onEnter} onLeave={onLeave}>
        <Styled.Link to={to}>
          <Styled.Container $variant={variant}>
            {/* @ts-expect-error FIXME: SC6 */}
            <Styled.Image $variant={variant} $image={image} />
            <div>
              {category && (
                // @ts-expect-error FIXME: SC6
                <Styled.Tag $variant={variant}>{category}</Styled.Tag>
              )}
              {title && (
                <Styled.Headline {...headlineSize}>{title}</Styled.Headline>
              )}
              {description && (
                <Styled.Description>{description}</Styled.Description>
              )}
            </div>
          </Styled.Container>
        </Styled.Link>
        {!!readingTime && (
          // @ts-expect-error FIXME: SC6
          <Styled.Caption size="small" noMargin $variant={variant}>
            {readingTime} Minute{readingTime > 1 && 'n'}
          </Styled.Caption>
        )}
      </Observed>
    </Styled.Card>
  );
};
